import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ethers } from 'ethers'
import { useAccount, useConnectorClient } from 'wagmi'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import RespectGraph from '../components/RespectGraph'
import { formatPR } from '../utils/prCurrency'
import { formatNumber } from '../utils/formatNumber'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { toast } from 'sonner'
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  ThumbsUp,
  ThumbsDown,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Youtube,
  Globe,
  MessageCircle,
  LogIn,
  Wallet,
  ShoppingCart,
  ShieldCheck,
  AlertCircle,
  XCircle,
  Link,
  ChevronDown,
  X,
  Copy,
  PlusCircle,
} from 'lucide-react'
import DarkModeToggle from '../components/DarkModeToggle'
import ShareButton from '../components/ShareButton'
import NotificationPanel from '../components/NotificationPanel'
import FeedCard from '../components/FeedCard'
import DiscussionModal from '../components/DiscussionModal'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function ProfileView() {
  const { username } = useParams()
  const navigate = useNavigate()
  const { token, address } = useAuth()
  const { isDarkMode } = useTheme()
  const { connector, isConnected } = useAccount()
  const { data: client } = useConnectorClient()
  const [profile, setProfile] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [respectAmount, setRespectAmount] = useState('')
  const [respectReason, setRespectReason] = useState('')
  const [isGivingRespect, setIsGivingRespect] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const isAuthenticated = !!token
  const [respectType, setRespectType] = useState('positive')
  const [pendingTransactions, setPendingTransactions] = useState([])
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false)
  
  // Discussion modal state
  const [discussionOpen, setDiscussionOpen] = useState(false)
  const [selectedTransactionId, setSelectedTransactionId] = useState(null)
  
  // New fields for enhanced respect giving
  const [respectLink, setRespectLink] = useState('')
  const [respectCategories, setRespectCategories] = useState([])
  const [allCategories, setAllCategories] = useState([])
  
  // Withdraw & Buy PR state (same as Home.js)
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false)
  const [showBuyDialog, setShowBuyDialog] = useState(false)
  const [buyAmount, setBuyAmount] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  
  // Wallet Optimization state
  const [showWalletCheck, setShowWalletCheck] = useState(false)
  const [walletPRBalance, setWalletPRBalance] = useState(0)
  const [isAssetAdded, setIsAssetAdded] = useState(true)
  const [contractCopied, setContractCopied] = useState(false)

  const CONTRACT = "0xfDbBBcc7dF9aad571f5c206E4Adb3E3aA4e30521"
  const POLYGON_CHAIN_ID = "0x89" // 137 in decimal
  const ABI = [
    "function buy(uint256 humanAmount) payable",
    "function withdrawWithSig(uint256,string,bytes) external",
    "function balanceOf(address) view returns (uint256)",
    "function pricePerTokenWei() view returns (uint256)"
  ]

  // ==== Provider / Signer helper (copied from Home.js) ====
  const getSignerOrThrow = async () => {
    if (connector && isConnected) {
      try {
        const provider = await connector.getProvider();
        if (provider) {
          console.log('✓ Using wagmi connector provider:', connector.id);
          const ethersProvider = new ethers.BrowserProvider(provider);
          return ethersProvider.getSigner();
        }
      } catch (e) {
        console.warn('Failed to get provider from wagmi connector:', e);
      }
    }

    let providerObject = null;
    if (typeof window !== 'undefined') {
      if (window.__PR_APP_PROVIDER) {
        providerObject = window.__PR_APP_PROVIDER;
        console.log('✓ Using saved provider from window.__PR_APP_PROVIDER');
      } else if (window.ethereum) {
        providerObject = window.ethereum;
        console.log('✓ Using window.ethereum provider');
      } else if (window.web3 && window.web3.currentProvider) {
        providerObject = window.web3.currentProvider;
        console.log('✓ Using window.web3.currentProvider');
      }
    }

    if (!providerObject) {
      console.error('❌ No provider found. Connector:', connector?.id, 'Connected:', isConnected);
      throw new Error('No web3 provider found. Please reconnect your wallet.');
    }

    const provider = new ethers.BrowserProvider(providerObject);

    try {
      if (providerObject.request) {
        await providerObject.request({ method: 'eth_requestAccounts' });
      } else if (providerObject.enable) {
        await providerObject.enable();
      } else if (providerObject.send) {
        await providerObject.send('eth_requestAccounts', []);
      }
    } catch (e) {
      console.error('Account request error:', e);
      throw new Error('Wallet connection request rejected or failed.');
    }

    return provider.getSigner();
  }

  const checkPolygonNetwork = async () => {
    let providerObject = null;
    
    if (connector && isConnected) {
      try {
        providerObject = await connector.getProvider();
        console.log('✓ Using wagmi connector for network check:', connector.id);
      } catch (e) {
        console.warn('Failed to get provider from wagmi:', e);
      }
    }
    
    if (!providerObject) {
      providerObject = window.__PR_APP_PROVIDER ?? window.ethereum;
    }
    
    if (!providerObject) {
      toast.error('No wallet provider found. Please reconnect your wallet.');
      return false;
    }

    try {
      const chainId = await providerObject.request({ method: 'eth_chainId' })
      if (chainId !== POLYGON_CHAIN_ID) {
        toast.info('Switching to Polygon Mainnet...')
        try {
          await providerObject.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: POLYGON_CHAIN_ID }],
          })
          return true
        } catch (switchError) {
          if (switchError.code === 4902) {
            await providerObject.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: POLYGON_CHAIN_ID,
                chainName: 'Polygon Mainnet',
                nativeCurrency: {
                  name: 'POL',
                  symbol: 'POL',
                  decimals: 18
                },
                rpcUrls: ['https://polygon-rpc.com/'],
                blockExplorerUrls: ['https://polygonscan.com/']
              }]
            })
            return true
          }
          throw switchError
        }
      }
      return true
    } catch (error) {
      console.error('Network switch error:', error);
      toast.error('Please switch to Polygon Mainnet in your wallet')
      return false
    }
  }

  const checkWalletPRBalance = async (userAddress) => {
    try {
      let providerObject = null;
      
      if (connector && isConnected) {
        try {
          providerObject = await connector.getProvider();
        } catch (e) {
          console.warn('Failed to get provider from wagmi for balance check:', e);
        }
      }
      
      if (!providerObject) {
        providerObject = window.__PR_APP_PROVIDER ?? window.ethereum;
      }
      
      if (!providerObject) return 0;
      
      const provider = new ethers.BrowserProvider(providerObject);
      const contract = new ethers.Contract(CONTRACT, ABI, provider);
      const balanceBn = await contract.balanceOf(userAddress);
      return Number(ethers.formatUnits(balanceBn, 18));
    } catch (error) {
      console.error('Error checking PR balance:', error);
      return 0;
    }
  }

  // Wallet Optimization Functions
  const handleAddTokenToWallet = async () => {
    try {
      let providerObject = null;
      
      if (connector && isConnected) {
        try {
          providerObject = await connector.getProvider();
        } catch (e) {
          console.warn('Failed to get provider from wagmi:', e);
        }
      }
      
      if (!providerObject) {
        providerObject = window.__PR_APP_PROVIDER ?? window.ethereum;
      }
      
      if (!providerObject) {
        toast.error('No wallet provider found');
        return;
      }

      await providerObject.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: CONTRACT,
            symbol: 'PR',
            decimals: 18,
            image: `${window.location.origin}/pr-logo.png`,
          },
        },
      });
      
      localStorage.setItem(`pr_asset_added_${address}`, 'true');
      setIsAssetAdded(true);
      toast.success('PR token added to your wallet!');
    } catch (error) {
      console.error('Error adding token:', error);
      toast.error('Failed to add token to wallet');
    }
  };

  const performWalletAudit = async () => {
    if (!address || !isConnected) return;
    
    // Check if user dismissed the popup permanently
    const dismissed = localStorage.getItem(`wallet_opt_dismissed_${address}`);
    if (dismissed === 'true') return;
    
    try {
      const bal = await checkWalletPRBalance(address);
      setWalletPRBalance(bal);
      const hasAdded = localStorage.getItem(`pr_asset_added_${address}`);
      setIsAssetAdded(!!hasAdded);

      // Show banner only if wallet needs optimization
      if (!hasAdded || bal < 1) {
        setShowWalletCheck(true);
      }
    } catch (e) { 
      console.error("Wallet audit failed", e); 
    }
  };

  const handleDismissWalletOptimization = () => {
    localStorage.setItem(`wallet_opt_dismissed_${address}`, 'true');
    setShowWalletCheck(false);
    toast.success("Got it! You won't see this message again.");
  };

  const handleWithdraw = async () => {
    const raw = withdrawAmount?.toString().trim();
    const parsed = Number(raw);

    if (!raw || Number.isNaN(parsed)) {
      toast.error('Please enter a valid numeric amount');
      return;
    }
    if (parsed < 1) {
      toast.error('Minimum withdrawal amount is 1 PR');
      return;
    }

    const humanAmountInt = Math.floor(parsed);
    if (humanAmountInt !== parsed) {
      toast.info(`Rounding down to ${humanAmountInt} PR (fractions not supported)`);
    }

    setIsProcessing(true);
    try {
      const isPolygon = await checkPolygonNetwork();
      if (!isPolygon) { setIsProcessing(false); return; }

      const signer = await getSignerOrThrow();
      const userAddress = await signer.getAddress();

      const walletPRBalance = await checkWalletPRBalance(userAddress);
      if (walletPRBalance < 1) {
        toast.error('You need at least 1 PR in your wallet to initiate withdrawal. Please buy PR first.');
        setShowWithdrawDialog(false);
        setShowBuyDialog(true);
        setIsProcessing(false);
        return;
      }

      // Check profile verification status
      const verificationStatus = profile?.verification_status || 'unverified';
      if (verificationStatus === 'unverified') {
        toast.error('Profile verification required for withdrawals. Redirecting to Edit Profile...');
        setShowWithdrawDialog(false);
        setIsProcessing(false);
        setTimeout(() => navigate('/edit-profile'), 1500);
        return;
      }
      if (verificationStatus === 'pending_review') {
        toast.info('Your profile verification is pending manual review. Withdrawals will be enabled once approved.');
        setShowWithdrawDialog(false);
        setIsProcessing(false);
        return;
      }

      const withdrawableBalance = profile?.withdrawable_balance || 0;
      if (withdrawableBalance < humanAmountInt) {
        toast.error(`Insufficient app balance. You have ${withdrawableBalance.toFixed(4)} PR available`);
        setIsProcessing(false);
        return;
      }

      toast.info('Requesting withdrawal signature...');
      const signResponse = await axios.post(
        `${API}/get-withdraw-sign`,
        { user: userAddress, amount: String(humanAmountInt) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { nonce, signature } = signResponse.data;
      if (!nonce || !signature) throw new Error('Invalid signature response from backend');

      toast.info('Submitting transaction to Polygon...');
      const contract = new ethers.Contract(CONTRACT, ABI, signer);

      const tx = await contract.withdrawWithSig(humanAmountInt, nonce, signature);
      if (tx && tx.hash) toast.info(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);
      const receipt = await tx.wait();
      const receiptHash = receipt?.transactionHash || receipt?.hash;
      if (receiptHash) toast.success(`Withdrawal successful! Hash: ${receiptHash.slice(0, 10)}...`);
      else toast.success('Withdrawal successful! Transaction mined.');

      await axios.post(
        `${API}/withdraw`,
        { amount: humanAmountInt },
        { headers: { Authorization: `Bearer ${token}` }, params: { amount: humanAmountInt } }
      );

      setShowWithdrawDialog(false);
      setWithdrawAmount('');
      fetchProfile();

    } catch (err) {
      console.error('Withdrawal error:', err);
      if (err.response?.data?.detail) toast.error(err.response.data.detail);
      else if (err.response?.data?.error) toast.error(err.response?.data?.error);
      else if (err.message) toast.error(err.message);
      else toast.error('Withdrawal failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }

  const handleBuyPR = async () => {
    const amount = parseInt(buyAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsProcessing(true);
    try {
      const isPolygon = await checkPolygonNetwork();
      if (!isPolygon) { setIsProcessing(false); return; }

      const signer = await getSignerOrThrow();
      const contract = new ethers.Contract(CONTRACT, ABI, signer);

      const priceBn = await contract.pricePerTokenWei();
      const priceBigInt = BigInt(priceBn.toString());
      const totalCost = priceBigInt * BigInt(amount);

      toast.info('Submitting purchase to Polygon...');
      const tx = await contract.buy(amount, { value: totalCost });
      if (tx && tx.hash) toast.info(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);
      await tx.wait();
      toast.success(`Successfully bought ${amount} PR!`);
      setShowBuyDialog(false);
      setBuyAmount('');
    } catch (err) {
      console.error('Buy PR error:', err);
      toast.error(err.response?.data?.error || err.message || 'Purchase failed');
    } finally {
      setIsProcessing(false);
    }
  }

  useEffect(() => {
    fetchProfile()
    fetchCategories()
  }, [username])

  // Check if viewing own profile
  const isOwnProfile = profile && address && 
    profile.wallet_address.toLowerCase() === address.toLowerCase()

  useEffect(() => {
    if (profile) {
      fetchPendingTransactions()
      // If viewing own profile, perform wallet audit
      if (profile && address && profile.wallet_address.toLowerCase() === address.toLowerCase()) {
        performWalletAudit()
      }
    }
  }, [profile, address])

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`)
      setAllCategories(response.data.categories || [])
    } catch (err) {
      console.error('Failed to fetch categories:', err)
    }
  }

  const handleCategoryToggle = (category) => {
    setRespectCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    )
  }

  const fetchProfile = async () => {
    setIsLoading(true)
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const response = await axios.get(`${API}/profiles/${username}`, { headers })
      setProfile(response.data)
    } catch (err) {
      toast.error('Failed to load profile')
      if (isAuthenticated) {
        navigate('/home')
      } else {
        navigate('/connect')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPendingTransactions = async () => {
    if (!profile) {
      console.log('Profile not loaded yet, skipping transaction fetch')
      return
    }

    setIsLoadingTransactions(true)
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      // Fetch from the new feed API which includes all enhanced data
      const response = await axios.get(`${API}/feed/public?page=1&limit=100`, { headers })
      
      // Filter to only show transactions where this profile is RECEIVING (not giving - for anonymity)
      const profileTransactions = response.data.transactions.filter(tx => {
        return tx.to_wallet.toLowerCase() === profile.wallet_address.toLowerCase()
      })
      
      setPendingTransactions(profileTransactions)
    } catch (err) {
      console.error('Failed to load pending transactions:', err)
      setPendingTransactions([])
    } finally {
      setIsLoadingTransactions(false)
    }
  }

  const handleVoteOnTransaction = async (transactionId, voteType) => {
    if (!isAuthenticated) {
      toast.error('Please connect your wallet to vote')
      navigate('/connect')
      return
    }

    try {
      await axios.post(
        `${API}/respect/vote/${transactionId}`,
        { vote: voteType },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { vote: voteType }
        }
      )
      toast.success(`Vote recorded! Transaction will process after 10 ${voteType === 'approve' ? 'approvals' : 'rejections'}.`)
      fetchPendingTransactions()
    } catch (err) {
      console.error('Vote error:', err)
      toast.error(err.response?.data?.detail || `Failed to ${voteType} transaction`)
    }
  }

  const openDiscussion = (transactionId) => {
    setSelectedTransactionId(transactionId)
    setDiscussionOpen(true)
  }

  const handleGiveRespect = async () => {
    if (!isAuthenticated) {
      toast.error('Please connect your wallet to give respect')
      navigate('/connect')
      return
    }

    const numAmount = parseInt(respectAmount)
    
    if (!respectAmount || numAmount <= 0) {
      toast.error('Please enter a valid respect amount')
      return
    }

    if (numAmount < 10) {
      toast.error('Minimum respect amount is 10')
      return
    }

    if (numAmount > 10000) {
      toast.error('Maximum respect amount is 10,000')
      return
    }

    if (!respectReason || respectReason.trim().length < 5) {
      toast.error('Please provide a reason (minimum 5 characters)')
      return
    }

    // Validate categories (at least one required)
    if (respectCategories.length === 0) {
      toast.error('Please select at least one category')
      return
    }

    setIsGivingRespect(true)
    try {
      const amount = respectType === 'positive' ? parseInt(respectAmount) : -parseInt(respectAmount)
      await axios.post(
        `${API}/respect/give`,
        {
          to_wallet: profile.wallet_address,
          respect_amount: amount,
          reason: respectReason.trim(),
          link: respectLink.trim() || null,
          categories: respectCategories,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      toast.success('Respect submitted! Awaiting community votes (10 required).')
      setShowDialog(false)
      setRespectAmount('')
      setRespectReason('')
      setRespectLink('')
      setRespectCategories([])
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to give respect')
    } finally {
      setIsGivingRespect(false)
    }
  }

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!profile) return null

  const socialLinks = profile.social_links || {}

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm border-b`}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            data-testid="back-button"
            onClick={() => isAuthenticated ? navigate('/home') : navigate('/connect')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {isAuthenticated ? 'Back to Home' : 'Connect Wallet'}
          </Button>
          <div className="flex items-center gap-3">
            {!isAuthenticated && (
              <Button onClick={() => navigate('/connect')} variant="default" size="sm" className="flex items-center gap-2">
                <LogIn className="w-4 h-4" />
                Connect to Interact
              </Button>
            )}
            {isAuthenticated && <NotificationPanel token={token} address={address} />}
            <ShareButton 
              url={`${window.location.origin}/profile/${profile.username}`}
              title={`Check out ${profile.name}'s profile on Public Respect`}
              variant="outline"
              size="sm"
            />
            <DarkModeToggle />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* RED FLAGGED Warning for Banned Users */}
        {profile.is_banned && (
          <div className="mb-6 bg-red-600 border-4 border-red-900 rounded-xl p-6 shadow-2xl">
            <div className="flex items-center justify-center gap-4">
              <XCircle className="w-16 h-16 text-white animate-pulse" />
              <div className="text-center">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-1">🚨 RED FLAGGED</h2>
                <p className="text-white text-lg font-semibold">This profile has been flagged for suspicious activity</p>
              </div>
              <XCircle className="w-16 h-16 text-white animate-pulse" />
            </div>
          </div>
        )}

        {!isAuthenticated && (
          <div className={`mb-6 p-4 ${isDarkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border rounded-lg`}>
            <p className={`text-sm ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
              <strong>Read-Only Mode:</strong> You're viewing this profile publicly. Connect your wallet to give respect and interact!
            </p>
          </div>
        )}

        {/* Profile Card */}
        <Card data-testid="profile-view-card" className={`mb-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm`}>
          <CardContent className="pt-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 mb-6">
              <Avatar className={`w-24 h-24 sm:w-32 sm:h-32 border-4 ${isDarkMode ? 'border-purple-400' : 'border-purple-200'}`}>
                {profile.profile_image ? (
                  <AvatarImage src={profile.profile_image} alt={profile.name} />
                ) : (
                  <AvatarFallback className="text-3xl sm:text-4xl font-bold bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                    {getInitials(profile.name)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1 text-center sm:text-left">
                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-center sm:justify-start gap-1 sm:gap-2 mb-2">
                  <h1 className={`text-2xl sm:text-4xl font-bold ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>{profile.name}</h1>
                  {/* Profile status badges - UNCLAIMED only hides when VERIFIED */}
                  {profile.is_claimable && profile.verification_status !== 'verified' ? (
                    profile.is_claimed ? (
                      <div className="flex items-center gap-1" title="Pending Claim Verification">
                        <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-orange-500" />
                        <span className="text-xs sm:text-sm text-orange-600 font-semibold">Pending Claim</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1" title="Unclaimed Profile - Can be claimed by rightful owner">
                        <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-yellow-500" />
                        <span className="text-xs sm:text-sm text-yellow-600 font-semibold">Unclaimed</span>
                      </div>
                    )
                  ) : profile.verification_status === 'verified' ? (
                    <div className="flex items-center gap-1" title="Verified Profile">
                      <ShieldCheck className="w-5 h-5 sm:w-8 sm:h-8 text-green-500" />
                      <span className="text-xs sm:text-sm text-green-600 font-semibold">Verified</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1" title="Unverified Profile">
                      <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-red-500" />
                      <span className="text-xs sm:text-sm text-red-600 font-semibold">Unverified</span>
                    </div>
                  )}
                </div>
                <p className={`text-lg sm:text-xl mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>@{profile.username}</p>
                {profile.bio && <p className={`text-base sm:text-lg mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{profile.bio}</p>}
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-4">
                  <Badge
                    data-testid="profile-respect-points"
                    className={`text-lg sm:text-xl px-4 py-2 sm:px-6 ${
                      profile.respect_points >= 100
                        ? 'bg-green-500'
                        : profile.respect_points >= 0
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                  >
                    {profile.respect_points >= 0 ? (
                      <><TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-2" /> +{formatNumber(profile.respect_points)} Respect</>
                    ) : (
                      <><TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 mr-2" /> {formatNumber(profile.respect_points)} Respect</>
                    )}
                  </Badge>
                  <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-4 py-2 sm:px-6 rounded-full text-base sm:text-lg font-semibold shadow-lg">
                    <img src="/pr-coin.png" alt="PR" className="w-5 h-5 sm:w-6 sm:h-6" />
                    <span>{formatPR(profile.respect_points)} PR</span>
                  </div>

                  {/* Withdraw & Buy PR buttons (only for own profile) */}
                  {isOwnProfile && (
                    <>
                      {/* Withdraw Button */}
                      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
                        <DialogTrigger asChild>
                          <button
                            type="button"
                            className={`flex items-center gap-1.5 bg-gradient-to-r from-green-400 to-emerald-500 text-white px-3 py-2 sm:px-4 rounded-full text-xs sm:text-sm font-semibold ${profile.is_banned ? 'opacity-50 cursor-not-allowed' : 'hover:from-green-500 hover:to-emerald-600'}`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            onClick={() => !profile.is_banned && console.log('Withdraw trigger clicked')}
                            onTouchStart={() => {}}
                            disabled={profile.is_banned}
                          >
                            <Wallet className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span>{(profile.withdrawable_balance || 0).toFixed(4)} PR</span>
                          </button>
                        </DialogTrigger>

                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Withdraw PR</DialogTitle>
                            <DialogDescription>
                              Available balance: {(profile.withdrawable_balance || 0).toFixed(4)} PR<br/>
                              <span className="text-xs text-orange-600">⚠️ Requires: At least 1 PR in your wallet + Polygon Mainnet</span>
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="withdraw-amount">Amount to Withdraw (minimum 1 PR, integers only)</Label>
                              <Input
                                id="withdraw-amount"
                                type="number"
                                min="1"
                                step="1"
                                value={withdrawAmount}
                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                placeholder="Enter amount (e.g., 10)"
                                className="mt-1"
                              />
                              <p className="text-xs text-gray-500 mt-1">Gas fee paid in POL. Fractional amounts will be rounded down.</p>
                            </div>
                            <Button
                              type="button"
                              onClick={handleWithdraw}
                              disabled={isProcessing}
                              className="w-full bg-green-500 hover:bg-green-600"
                            >
                              {isProcessing ? 'Processing...' : 'Withdraw to Wallet'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {/* Buy PR Button */}
                      <Dialog open={showBuyDialog} onOpenChange={setShowBuyDialog}>
                        <DialogTrigger asChild>
                          <button
                            type="button"
                            className={`flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-pink-500 px-3 py-1.5 rounded-md text-white text-sm font-semibold ${profile.is_banned ? 'opacity-50 cursor-not-allowed' : 'hover:from-purple-600 hover:to-pink-600'}`}
                            onClick={() => !profile.is_banned && console.log('Buy trigger clicked')}
                            onTouchStart={() => {}}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            disabled={profile.is_banned}
                          >
                            <ShoppingCart className="w-4 h-4" />
                            <span>Buy PR</span>
                          </button>
                        </DialogTrigger>

                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Buy PR</DialogTitle>
                            <DialogDescription>
                              Price: 5 POL per PR token
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="buy-amount">Amount to Buy</Label>
                              <Input
                                id="buy-amount"
                                type="number"
                                value={buyAmount}
                                onChange={(e) => setBuyAmount(e.target.value)}
                                placeholder="Enter amount"
                                className="mt-1"
                              />
                            </div>
                            <div className="text-sm text-gray-600">
                              Total cost: {buyAmount ? (parseInt(buyAmount) * 5).toFixed(2) : '0'} POL
                            </div>
                            <Button
                              type="button"
                              onClick={handleBuyPR}
                              disabled={isProcessing}
                              className="w-full bg-purple-500 hover:bg-purple-600"
                            >
                              {isProcessing ? 'Processing...' : 'Buy Now'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Social Links - Only show confirmed links for verified users, or all links for unverified/pending */}
            {(() => {
              const confirmedLinks = profile.confirmed_links || []
              const isVerified = profile.verification_status === 'verified'
              
              // For verified users, only show confirmed links. For others, show all links.
              const linksToShow = isVerified 
                ? Object.keys(socialLinks).filter(key => confirmedLinks.includes(key) && socialLinks[key])
                : Object.keys(socialLinks).filter(key => socialLinks[key])
              
              return linksToShow.length > 0 && (
                <div className="border-t pt-6">
                  <div className="flex items-center gap-2 mb-3 justify-center sm:justify-start">
                    <h3 className="text-base sm:text-lg font-semibold text-center sm:text-left" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Social Links</h3>
                    {isVerified ? (
                      <ShieldCheck className="w-5 h-5 text-green-500" title="Verified" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500" title="Unverified" />
                    )}
                  </div>
                  <div className={`mb-3 p-3 rounded-lg ${isVerified ? (isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200') : (isDarkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200')}`}>
                    <p className={`text-sm ${isVerified ? (isDarkMode ? 'text-green-200' : 'text-green-800') : (isDarkMode ? 'text-red-200' : 'text-red-800')}`}>
                      {isVerified ? '✓ Social links are verified and safe to check' : '⚠ These social links are not verified. Access at your own risk'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-3 justify-center sm:justify-start">
                    {((!isVerified || confirmedLinks.includes('facebook')) && socialLinks.facebook) && (
                    <a
                      href={socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs sm:text-sm"
                    >
                      <Facebook className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Facebook</span>
                    </a>
                  )}
                  {((!isVerified || confirmedLinks.includes('instagram')) && socialLinks.instagram) && (
                    <a
                      href={socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-pink-500 hover:bg-pink-600 text-white rounded-lg text-xs sm:text-sm"
                    >
                      <Instagram className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Instagram</span>
                    </a>
                  )}
                  {((!isVerified || confirmedLinks.includes('twitter')) && socialLinks.twitter) && (
                    <a
                      href={socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-black hover:bg-gray-800 text-white rounded-lg text-xs sm:text-sm"
                    >
                      <Twitter className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">X</span>
                    </a>
                  )}
                  {((!isVerified || confirmedLinks.includes('linkedin')) && socialLinks.linkedin) && (
                    <a
                      href={socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs sm:text-sm"
                    >
                      <Linkedin className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">LinkedIn</span>
                    </a>
                  )}
                  {((!isVerified || confirmedLinks.includes('reddit')) && socialLinks.reddit) && (
                    <a
                      href={socialLinks.reddit}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs sm:text-sm"
                    >
                      <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Reddit</span>
                    </a>
                  )}
                  {((!isVerified || confirmedLinks.includes('youtube')) && socialLinks.youtube) && (
                    <a
                      href={socialLinks.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs sm:text-sm"
                    >
                      <Youtube className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">YouTube</span>
                    </a>
                  )}
                  {((!isVerified || confirmedLinks.includes('tiktok')) && socialLinks.tiktok) && (
                    <a
                      href={socialLinks.tiktok}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 hover:from-pink-600 hover:via-red-600 hover:to-yellow-600 text-white rounded-lg text-xs sm:text-sm"
                    >
                      <span className="text-sm">🎵</span>
                      <span className="hidden sm:inline">TikTok</span>
                    </a>
                  )}
                  {((!isVerified || confirmedLinks.includes('website')) && socialLinks.website) && (
                    <a
                      href={socialLinks.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-gray-700 hover:bg-gray-800 text-white rounded-lg text-xs sm:text-sm"
                    >
                      <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Website</span>
                    </a>
                  )}
                  </div>
                </div>
              )
            })()}
          </CardContent>
        </Card>

        {/* Give Respect Section - MOVED BEFORE GRAPH */}
        {!isOwnProfile && (
          <Card className={`mb-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm`}>
            <CardHeader>
              <CardTitle className={isDarkMode ? 'text-gray-100' : ''} style={{fontFamily: 'Space Grotesk, sans-serif'}}>Give Respect</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="give-positive-respect-button"
                      onClick={() => {
                        if (!profile.is_banned) {
                          setRespectType('positive')
                          setShowDialog(true)
                        }
                      }}
                      disabled={profile.is_banned}
                      className={`flex-1 h-14 sm:h-16 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold text-base sm:text-lg ${profile.is_banned ? 'opacity-50 cursor-not-allowed' : 'hover:from-green-600 hover:to-emerald-600'}`}
                    >
                      <ThumbsUp className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                      <span className="hidden sm:inline">Give Positive Respect</span>
                      <span className="sm:hidden">Positive</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {respectType === 'positive' ? 'Give Positive Respect' : 'Give Negative Respect'}
                      </DialogTitle>
                      <DialogDescription>
                        {respectType === 'positive'
                          ? 'Give positive respect to show appreciation. You will pledge 12% of the respect amount (2% base + 10% for community voters).'
                          : 'Give negative respect to show disapproval. You will pledge 12% of the respect amount (2% base + 10% for community voters).'}<br/>
                        <strong className="text-orange-600 mt-2 block">⚠️ Warning:</strong> All transactions are audited. Heavy fines and permanent bans will be imposed for fraud, artificial respect manipulation, or coordinated defamation attempts.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="respect-amount">Respect Amount (Min: 10, Max: 10,000)</Label>
                        <Input
                          data-testid="respect-amount-input"
                          id="respect-amount"
                          type="number"
                          min="10"
                          max="10000"
                          value={respectAmount}
                          onChange={(e) => setRespectAmount(e.target.value)}
                          placeholder="Enter amount (10-10,000)"
                          className="mt-1"
                        />
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          You'll pledge 12% of the amount being given as collateral
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="respect-reason">Why? (Required)</Label>
                        <Textarea
                          data-testid="respect-reason-input"
                          id="respect-reason"
                          value={respectReason}
                          onChange={(e) => setRespectReason(e.target.value)}
                          placeholder="Explain why you're giving this respect (min 5 characters)..."
                          className="mt-1"
                          rows={3}
                        />
                      </div>
                      
                      {/* Link Field (Optional) */}
                      <div>
                        <Label htmlFor="respect-link">
                          <div className="flex items-center gap-2">
                            <Link className="w-4 h-4" />
                            Social Media Link (Optional)
                          </div>
                        </Label>
                        <Input
                          data-testid="respect-link-input"
                          id="respect-link"
                          type="url"
                          value={respectLink}
                          onChange={(e) => setRespectLink(e.target.value)}
                          placeholder="https://youtube.com/... or facebook.com/..."
                          className="mt-1"
                        />
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Only Facebook, YouTube, Instagram, TikTok, Reddit, LinkedIn, and X links allowed
                        </p>
                      </div>
                      
                      {/* Categories Field (Required) */}
                      <div>
                        <Label>Categories (Required) *</Label>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="w-full mt-1 justify-between"
                              data-testid="categories-dropdown"
                            >
                              {respectCategories.length === 0 
                                ? 'Select categories...' 
                                : `${respectCategories.length} selected`
                              }
                              <ChevronDown className="w-4 h-4 ml-2" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-56 max-h-60 overflow-y-auto">
                            {allCategories.map(category => (
                              <DropdownMenuCheckboxItem
                                key={category}
                                checked={respectCategories.includes(category)}
                                onCheckedChange={() => handleCategoryToggle(category)}
                              >
                                {category}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        
                        {/* Selected categories display */}
                        {respectCategories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {respectCategories.map(cat => (
                              <Badge 
                                key={cat} 
                                variant="secondary" 
                                className="cursor-pointer text-xs"
                                onClick={() => handleCategoryToggle(cat)}
                              >
                                {cat}
                                <X className="w-3 h-3 ml-1" />
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <Button
                        data-testid="submit-respect-button"
                        onClick={handleGiveRespect}
                        disabled={isGivingRespect}
                        className={`w-full ${
                          respectType === 'positive'
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                            : 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600'
                        }`}
                      >
                        {isGivingRespect ? 'Processing...' : 'Confirm'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  data-testid="give-negative-respect-button"
                  onClick={() => {
                    if (!profile.is_banned) {
                      setRespectType('negative')
                      setShowDialog(true)
                    }
                  }}
                  disabled={profile.is_banned}
                  className={`flex-1 h-14 sm:h-16 bg-gradient-to-r from-red-500 to-rose-500 text-white font-semibold text-base sm:text-lg ${profile.is_banned ? 'opacity-50 cursor-not-allowed' : 'hover:from-red-600 hover:to-rose-600'}`}
                >
                  <ThumbsDown className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  <span className="hidden sm:inline">Give Negative Respect</span>
                  <span className="sm:hidden">Negative</span>
                </Button>
              </div>
              <p className={`text-xs sm:text-sm mt-4 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>Decentralized Voting Rules:</strong><br/>
                • You pledge 12% of the respect amount being given (2% base + 10% for community voters)<br/>
                • Requires 10 community approvals or rejections to process<br/>
                • If <strong>approved</strong>: Receiver gets respect (minus 10% for voters), you get 20% bonus + pledge back<br/>
                • If <strong>rejected</strong>: Receiver gets 2%, you lose 10% (given to voters)<br/>
                • Each of 10 voters gets 1% reward from the losing party<br/>
                <strong className="text-red-600">⚠️ All transactions are audited. Fraud, artificial manipulation, or coordinated attacks will result in heavy fines and permanent bans.</strong>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Respect Graph - NOW AFTER GIVE RESPECT */}
        <div className="mb-6">
          <RespectGraph username={username} token={token} isDarkMode={isDarkMode} />
        </div>

        {/* Transactions Involving This Profile */}
        <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm mt-6`}>
          <CardHeader>
            <CardTitle className={isDarkMode ? 'text-gray-100' : ''} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Respect Received
            </CardTitle>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              All respect transactions received by this profile
            </p>
          </CardHeader>
          <CardContent>
            {isLoadingTransactions ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : pendingTransactions.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <p>No transactions involving this profile</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingTransactions.map((tx) => (
                  <FeedCard
                    key={tx.id}
                    transaction={tx}
                    currentWallet={address}
                    onVote={handleVoteOnTransaction}
                    onDiscuss={openDiscussion}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Discussion Modal */}
        <DiscussionModal
          open={discussionOpen}
          transactionId={selectedTransactionId}
          onClose={() => setDiscussionOpen(false)}
        />
        
        {/* Wallet Optimization Banner - Only show on own profile */}
        {isOwnProfile && showWalletCheck && (
          <div className={`fixed bottom-0 left-0 right-0 z-40 ${isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-white border-t border-gray-200'} shadow-lg`}>
            <div className="max-w-7xl mx-auto px-4 py-4">
              {/* Header with close button */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Wallet className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    Wallet Optimization
                  </p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setShowWalletCheck(false)}
                  className={`h-6 w-6 p-0 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500'}`}
                >
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Description */}
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Ensure your wallet is ready to interact with Public Respect:
              </p>
              
              {/* Steps */}
              <div className="space-y-2 mb-3">
                {/* Step 1: Add Token */}
                <div className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    {isAssetAdded ? (
                      <ShieldCheck className="w-4 h-4 text-green-500" />
                    ) : (
                      <PlusCircle className="w-4 h-4 text-blue-500" />
                    )}
                    <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Add PR Token to your wallet
                    </span>
                  </div>
                  {!isAssetAdded && (
                    <Button size="sm" variant="outline" onClick={handleAddTokenToWallet} className="text-xs h-7">
                      Add Token
                    </Button>
                  )}
                </div>
                
                {/* Step 2: Buy PR */}
                <div className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    {walletPRBalance >= 1 ? (
                      <ShieldCheck className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-orange-500" />
                    )}
                    <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Buy PR Token - Need at least 1 PR for withdrawals ({walletPRBalance.toFixed(2)} PR)
                    </span>
                  </div>
                  {walletPRBalance < 1 && (
                    <Button 
                      size="sm" 
                      onClick={() => { setShowWalletCheck(false); setShowBuyDialog(true); }} 
                      className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700"
                    >
                      <ShoppingCart className="w-3 h-3 mr-1" />
                      Buy PR
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Mobile: Manual import info */}
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} space-y-2 mb-3`}>
                <p className="sm:hidden">Please import the token manually if needed:</p>
                <div className={`flex items-center gap-2 p-2 rounded text-xs font-mono ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <span className="truncate flex-1">0xfDbBBcc7dF9aad571f5c206E4Adb3E3aA4e30521</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2"
                    onClick={() => {
                      navigator.clipboard.writeText('0xfDbBBcc7dF9aad571f5c206E4Adb3E3aA4e30521')
                      setContractCopied(true)
                      setTimeout(() => setContractCopied(false), 2000)
                      toast.success('Contract address copied!')
                    }}
                  >
                    {contractCopied ? (
                      <ShieldCheck className="w-3 h-3 text-green-500" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </Button>
                </div>
                <p className={`italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Can take 5 mins to update if already purchased.
                </p>
              </div>
              
              {/* All Done Button */}
              <Button 
                onClick={handleDismissWalletOptimization}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                All Done
              </Button>
              <p className={`text-xs text-center mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Don't show this message again
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
